import { Button, Card, Empty, Skeleton, Table, Typography } from 'antd'
import classnames from 'classnames'
import React, { useState } from 'react'
import { Column, Row } from 'react-display-flex'
import useSWR from 'swr'

import { fetchByDomain } from '../../../api'
import { SelectTenant } from '../../../components'
import { bespokeRatingsTableColumns } from './bespoke-ratings-table-columns'
import { CreateBespokeRatingDrawer } from './CreateBespokeRating'
import { DeleteBespokeRatingModal } from './DeleteBespokeRatingModal/DeleteBespokeRfqEmailModal'
import { BespokeRating } from './models'
import { UpdateBespokeRatingDrawer } from './UpadateBespokeRating'

export const BespokeRatings = () => {
  const [currentTenant, setCurrentTenant] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [bespokeRatingToUpdate, setBespokeRatingToUpdate] = useState(null)
  const [bespokeRatingToDelete, setBespokeRatingToDelete] = useState(null)

  const {
    data: bespokeRatings,
    mutate,
    error,
  } = useSWR(currentTenant ? ['/banking-groups/compliance-rules', currentTenant] : null, fetchByDomain)

  const columns = bespokeRatingsTableColumns({ onUpdate: setBespokeRatingToUpdate, onDelete: setBespokeRatingToDelete })

  const tableItems = convertBespokeRatingsToTableItems(bespokeRatings)

  const onCloseDialogs = () => {
    setIsCreating(false)
    setBespokeRatingToUpdate(null)
    setBespokeRatingToDelete(null)
  }

  const onSuccess = () => {
    onCloseDialogs()
    mutate()
  }

  if (error) {
    return (
      <Empty description="Something wrong happen">
        {error?.error_message || error?.message || 'We have an error retrieving bespoke ratings'}
      </Empty>
    )
  }

  return (
    <Column className="trades">
      <Typography.Title>Bespoke Rating</Typography.Title>

      {currentTenant && (
        <Row justifyContentEnd>
          <Button type="primary" onClick={() => setIsCreating(true)}>
            Create Bespoke Rating
          </Button>
        </Row>
      )}

      <Card>
        <SelectTenant onTenantSelected={setCurrentTenant} />
      </Card>

      {!currentTenant && (
        <Card>
          <Typography.Title level={3}>Select a tenant</Typography.Title>
          <Typography>Please select one tenant to show the bespoke ratings</Typography>
        </Card>
      )}

      {currentTenant && !error && !bespokeRatings && <Skeleton />}

      {currentTenant && bespokeRatings && (
        <Table
          rowClassName={(bespokeRating: BespokeRating) =>
            classnames({ active: bespokeRating.id === (bespokeRatingToDelete?.id || bespokeRatingToUpdate?.id) })
          }
          dataSource={tableItems}
          loading={!bespokeRatings}
          columns={columns}
          pagination={false}
          rowKey="id"
          size="small"
        />
      )}

      <CreateBespokeRatingDrawer
        domain={currentTenant}
        isVisible={isCreating}
        onClose={onCloseDialogs}
        onSuccess={onSuccess}
      />

      <UpdateBespokeRatingDrawer
        domain={currentTenant}
        bespokeRating={bespokeRatingToUpdate}
        isVisible={bespokeRatingToUpdate}
        onClose={onCloseDialogs}
        onSuccess={onSuccess}
      />

      <DeleteBespokeRatingModal
        domain={currentTenant}
        bespokeRating={bespokeRatingToDelete}
        isVisible={bespokeRatingToDelete}
        onClose={onCloseDialogs}
        onSuccess={onSuccess}
      />
    </Column>
  )
}

const convertBespokeRatingsToTableItems = (bespokeRatings: BespokeRating[]) =>
  bespokeRatings?.map((bespokeRating) => ({
    ...bespokeRating,
    bankingGroup: `${bespokeRating.bankingGroupId} - ${bespokeRating.name}`,
  })) || []
